import { ExecutionDeadlineFormDataType } from '../schema';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from '../translations/DeadlinesTable.translations.json';
import { CellContext, createColumnHelper } from '@tanstack/react-table';
import { FC, useCallback, useMemo, useState } from 'react';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { getRowHtmlAttributes } from './utils';
import { formatDate, getUtcDayjs, ISODate } from '../../common/utils';
import { Table } from '../../common/tanstacktable';
import { DeadlinesPeriodCheckbox } from './DeadlinesPeriodCheckbox';
import styled from 'styled-components';
import { Button, Flex } from 'antd';
import { DeadlinesCheckModal } from './DeadlinesCheckModal';
import { useValidateDeadlines } from './query/useValidateDeadlines';
import { ApiError } from '../../common/ApiClient';

const { addTranslations, useTranslation } = i18nNsFactory(
  'deadlines-view-table',
);
addTranslations(translations);
const columnHelper = createColumnHelper<ExecutionDeadlineFormDataType>();
const defaultData: ExecutionDeadlineFormDataType[] = [];

export interface DeadlinesViewTableProps {
  dataObjectVersionId: number;
  deadlines?: ExecutionDeadlineFormDataType[];
  onDeadlineValidate: () => void;
  onDeadlineValidateErrors: (errors: ApiError[]) => void;
  onDeadlineValidateSuccess: () => void;
}

export function DeadlinesViewTable({
  dataObjectVersionId,
  deadlines,
  onDeadlineValidate,
  onDeadlineValidateErrors,
  onDeadlineValidateSuccess,
}: DeadlinesViewTableProps) {
  const { t } = useTranslation();
  const [isPastYear, setIsPastYear] = useState(true);
  const [isCheckModalVisible, setIsCheckModalVisible] = useState(false);
  const [checkError, setCheckError] = useState<string | undefined>();
  const { mutateAsync: validateAsync, isPending: isValidating } =
    useValidateDeadlines();

  const onChange = useCallback(
    (e: CheckboxChangeEvent) => {
      setIsPastYear(e.target.checked);
    },
    [setIsPastYear],
  );
  const rowHtmlAttributes = useCallback(
    (row: ExecutionDeadlineFormDataType) =>
      getRowHtmlAttributes(row.valueDate, isPastYear),
    [isPastYear],
  );

  const onCheck = useCallback(
    (dateRange: [ISODate, ISODate]) => {
      setCheckError(undefined);
      onDeadlineValidate();

      const [from, to] = [getUtcDayjs(dateRange[0]), getUtcDayjs(dateRange[1])];
      const valueDatesWithinRange = (deadlines ?? [])
        .map((deadline) => deadline.valueDate)
        .filter((valueDate) =>
          getUtcDayjs(valueDate).isBetween(from, to, 'day', '[]'),
        );

      if (valueDatesWithinRange.length === 0) {
        setCheckError(t('check.error.no-deadlines'));
        return;
      }

      return validateAsync({
        dataObjectVersionId,
        period: dateRange,
      })
        .then(() => {
          onDeadlineValidateSuccess();
          setIsCheckModalVisible(false);
        })
        .catch((error) => {
          onDeadlineValidateErrors(error.response?.data.errors ?? []);
          setIsCheckModalVisible(false);
          throw error;
        });
    },
    [
      dataObjectVersionId,
      deadlines,
      onDeadlineValidate,
      onDeadlineValidateErrors,
      onDeadlineValidateSuccess,
      t,
      validateAsync,
    ],
  );

  const onCheckCancel = useCallback(() => {
    setCheckError(undefined);
    setIsCheckModalVisible(false);
  }, []);

  const columns = useMemo(
    () => [
      columnHelper.accessor((rowData) => formatDate(rowData.valueDate), {
        id: 'valueDate',
        header: t('column.valueDate'),
        meta: { width: 120 },
        cell: Cell,
        enableColumnFilter: false,
        sortingFn: 'formattedDate',
      }),
      columnHelper.accessor(
        (rowData) =>
          rowData.dueDate
            ? `${formatDate(rowData.dueDate)} ${rowData.dueTime}`
            : rowData.dueTime,
        {
          id: 'dueDate',
          header: t('column.dueDate'),
          cell: Cell,
          enableColumnFilter: false,
          meta: {
            width: 250,
          },
          sortingFn: 'formattedDateTime',
        },
      ),
    ],
    [t],
  );

  return (
    <>
      <TableWrapper vertical gap="middle">
        <Table
          name="executionDeadlines"
          actions={
            <>
              <DeadlinesPeriodCheckbox
                onChange={onChange}
                showPastYear={isPastYear}
              />
              <Button
                id="check-deadlines"
                onClick={() => setIsCheckModalVisible(true)}
              >
                {t('button.check-deadlines.title')}
              </Button>
            </>
          }
          actionsInFront
          columns={columns}
          data={deadlines ?? defaultData}
          defaultSort={[{ id: 'valueDate', desc: true }]}
          rowHtmlAttributes={rowHtmlAttributes}
          sticky
          virtual
          scrollContainerSelector="#detail-layout-main-content"
        />
      </TableWrapper>
      {isCheckModalVisible && (
        <DeadlinesCheckModal
          onCheck={onCheck}
          onCancel={onCheckCancel}
          checkError={checkError}
          isValidating={isValidating}
        />
      )}
    </>
  );
}

const Cell: FC<CellContext<ExecutionDeadlineFormDataType, any>> = (props) =>
  props.renderValue();

const TableWrapper = styled(Flex)`
  width: 100%;
`;
